// "New backlog item": type, title, bug details (steps / expected / what happened) or a description,
// screenshots (paste, drop or choose; mark up before saving), area, owner, priority, size, where it ships
// and firm feedback to link. Warns when the title looks like an existing item.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Bell, ImagePlus, PenLine, X } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { Banner, Button, ChoiceChip, Dialog, Select, TextArea, TextField, TokenChip } from '../../ui';
import { NotifyNote } from '../feedback/Notify';
import { AREAS, firmLabel, fmtCalendarDate, type QueueRow } from '../feedback/types';
import { MarkupEditor } from './Markup';
import {
  appendFiles, FILE_ACCEPT, IMAGE_TYPES, KIND_LABEL, KINDS, MAX_FILE_BYTES, pastedImages, PRIORITIES, PRIORITY_LABEL, releaseOption,
  SIZE_LABEL, type BacklogData, type ItemDetail, type ItemKind, type Priority, type Size,
} from './types';

export type NewItemDefaults = {
  kind?: ItemKind; title?: string; area?: string | null; release_id?: string | null; owner_id?: string | null; description?: string;
  /** The epic (or story, for a sub-task) it goes under. */
  parent_id?: string | null;
  /** status and estimate: whether linking it to a dated release would notify (open, and the date changes). */
  request?: { id: string; reference: string; title: string; firm: string; status?: string; estimate?: string | null };
};

type Pending = { key: string; file: File; url: string | null; annotated?: boolean };
const words = (s: string) => new Set(s.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 3));

export function NewItemDialog({ open, onClose, onCreated, defaults, data }: {
  open: boolean; onClose: () => void; onCreated: (item: ItemDetail) => void; defaults?: NewItemDefaults; data?: BacklogData | null;
}) {
  const [loaded, setLoaded] = useState<BacklogData | null>(data ?? null);
  const [kind, setKind] = useState<ItemKind>('bug_fix');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [expected, setExpected] = useState('');
  const [actual, setActual] = useState('');
  const [area, setArea] = useState('');
  const [foundIn, setFoundIn] = useState('production');
  const [ownerId, setOwnerId] = useState('');
  const [priority, setPriority] = useState<Priority>('medium');
  const [size, setSize] = useState<Size | null>(null);
  const [releaseId, setReleaseId] = useState('');
  const [parentId, setParentId] = useState('');
  const [requests, setRequests] = useState<NonNullable<NewItemDefaults['request']>[]>([]);
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState<QueueRow[]>([]);
  const [files, setFiles] = useState<Pending[]>([]);
  const [marking, setMarking] = useState<Pending | null>(null);
  const [another, setAnother] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  useEffect(() => { if (data) setLoaded(data); }, [data]);
  useEffect(() => {
    if (!open || loaded) return;
    api.get<BacklogData>('/api/admin/backlog').then(setLoaded, () => {});
  }, [open, loaded]);

  const reset = (keepKind = false) => {
    if (!keepKind) setKind(defaults?.kind ?? 'bug_fix');
    setTitle(defaults?.title ?? ''); setDescription(defaults?.description ?? ''); setSteps(''); setExpected(''); setActual('');
    setArea(defaults?.area ?? ''); setOwnerId(defaults?.owner_id ?? ''); setPriority('medium'); setSize(null);
    setReleaseId(defaults?.release_id ?? ''); setParentId(defaults?.parent_id ?? ''); setRequests(defaults?.request ? [defaults.request] : []); setQ('');
    files.forEach((f) => f.url && URL.revokeObjectURL(f.url)); setFiles([]); setError(null);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) reset(); }, [open]);

  useEffect(() => {
    if (!q.trim()) { setMatches([]); return; }
    const t = window.setTimeout(() => {
      api.get<{ requests: QueueRow[] }>(`/api/admin/feedback?q=${encodeURIComponent(q.trim())}`).then((r) => setMatches(r.requests.slice(0, 6)), () => setMatches([]));
    }, 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const similar = useMemo(() => {
    const mine = words(title);
    if (mine.size < 2 || !loaded) return [];
    return loaded.items.map((i) => ({ i, n: [...words(i.title)].filter((w) => mine.has(w)).length })).filter((x) => x.n >= 2)
      .sort((a, b) => b.n - a.n).slice(0, 2).map((x) => x.i);
  }, [title, loaded]);

  const add = (list: File[]) => {
    const ok: Pending[] = [];
    for (const f of list) {
      if (f.size > MAX_FILE_BYTES) { setError(`${f.name} is over 10 MB.`); continue; }
      ok.push({ key: `${f.name}-${f.size}-${Math.random()}`, file: f, url: IMAGE_TYPES.includes(f.type) ? URL.createObjectURL(f) : null });
    }
    setFiles((cur) => [...cur, ...ok].slice(0, 10));
  };

  const submit = async () => {
    if (!title.trim()) { setError('Give the item a title.'); return; }
    setBusy(true); setError(null);
    const body = {
      kind, title: title.trim(), priority, size, area: area || null, owner_id: ownerId || null,
      // An epic isn't scheduled and has no parent; a sub-task (parent from the defaults) ships with its parent.
      release_id: kind === 'epic' || subtask ? null : releaseId || null, parent_id: kind === 'epic' ? null : parentId || null,
      request_ids: requests.map((r) => r.id),
      ...(kind === 'bug_fix'
        ? { steps: steps.trim() || null, expected: expected.trim() || null, actual: actual.trim() || null, found_in: foundIn || null, description: description.trim() || null }
        : { description: description.trim() || null }),
    };
    try {
      let res: { item: ItemDetail };
      if (files.length) {
        const form = new FormData();
        form.append('item', JSON.stringify(body));
        appendFiles(form, files.map((f) => ({ file: f.file, annotated: f.annotated })));
        res = await api.post<{ item: ItemDetail }>('/api/admin/backlog', form);
      } else res = await api.post<{ item: ItemDetail }>('/api/admin/backlog', body);
      onCreated(res.item);
      if (another) { reset(true); setLoaded((d) => (d ? { ...d, items: [...d.items, res.item] } : d)); } else onClose();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'The item could not be saved.'); }
    finally { setBusy(false); }
  };

  const bug = kind === 'bug_fix';
  const epics = loaded?.epics ?? [];
  // A parent from the defaults that isn't an epic makes this a sub-task (added from a story's Sub-tasks).
  const subtask = !!parentId && !epics.some((e) => e.id === parentId);
  const team = loaded?.team ?? [];
  const releases = loaded?.releases ?? [];
  // Linking feedback to a dated release sets its estimate, and the people on it hear the date (planning.ts).
  const ships = releases.find((r) => r.id === releaseId);
  const dated = ships?.kind === 'release' && ships.target_date ? ships.target_date : null;
  const told = dated ? requests.filter((r) => (!r.status || ['received', 'planned', 'in_progress'].includes(r.status)) && r.estimate !== dated) : [];
  return (
    <Dialog open={open} onClose={() => { if (!marking) onClose(); }} title="New backlog item" wide
      footer={(
        <>
          <label className="mr-auto flex items-center gap-2 text-sm text-ink-2"><input type="checkbox" checked={another} onChange={(e) => setAnother(e.target.checked)} className="h-4 w-4 accent-brand" />Create another</label>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy || !title.trim()} onClick={() => void submit()}>{told.length > 0 && <Bell aria-hidden size={16} />}{busy ? 'Saving…' : 'Add to backlog'}</Button>
        </>
      )}>
      <div
        className="flex flex-col gap-4"
        onPaste={(e) => { const imgs = pastedImages(e); if (imgs.length) { e.preventDefault(); add(imgs); } }}
        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void submit(); } }}
      >
        {error && <Banner tone="error" role="alert">{error}</Banner>}
        <fieldset className="m-0 border-0 p-0">
          <legend className="mb-2 p-0 text-[15px] font-semibold">Type</legend>
          <div className="flex flex-wrap gap-2">{KINDS.map((k) => <ChoiceChip key={k} pressed={kind === k} onClick={() => setKind(k)}>{KIND_LABEL[k]}</ChoiceChip>)}</div>
        </fieldset>
        <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} autoFocus />
        {similar.length > 0 && (
          <div role="note" className="rounded-[10px] bg-warn-bg px-3 py-2 text-[13px] text-warn-fg">
            <b>Looks similar:</b> {similar.map((s, n) => <span key={s.id}>{n > 0 && ' · '}<a href={`/admin/backlog/${s.id}`} target="_blank" rel="noreferrer" className="font-semibold text-warn-fg">{s.reference}</a> {s.title}</span>)}
          </div>
        )}
        {bug ? (
          <div className="flex flex-col gap-3 rounded-xl border border-line bg-panel p-3">
            <TextArea label="Steps to reproduce" optional rows={2} value={steps} onChange={(e) => setSteps(e.target.value)} maxLength={2000} />
            <div className="grid gap-3 sm:grid-cols-2">
              <TextArea label="Expected" optional rows={2} value={expected} onChange={(e) => setExpected(e.target.value)} maxLength={2000} />
              <TextArea label="What happened" optional rows={2} value={actual} onChange={(e) => setActual(e.target.value)} maxLength={2000} />
            </div>
          </div>
        ) : (
          <TextArea label="What should it do, and why do firms need it?" optional rows={4} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={5000} />
        )}

        <div className="flex flex-col gap-1.5">
          <div className="flex items-baseline justify-between gap-2"><span className="text-[15px] font-semibold">Screenshots and files</span><span className="text-xs text-muted">Up to 10 · images, PDF, Office, CSV · 10 MB each</span></div>
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); add([...e.dataTransfer.files]); }}
            className={`flex flex-wrap gap-2.5 rounded-xl border-[1.5px] border-dashed p-2.5 ${drag ? 'border-ok bg-ok-bg' : 'border-brand-ring bg-brand-tint'}`}
          >
            {files.map((f) => (
              <figure key={f.key} className="m-0 flex w-[150px] flex-col gap-1">
                <div className="relative flex h-24 items-center justify-center overflow-hidden rounded-lg border border-line bg-white">
                  {f.url ? <img src={f.url} alt="" className="h-full w-full object-cover" /> : <span className="text-xs font-bold text-bad">{f.file.name.split('.').pop()?.toUpperCase()}</span>}
                  <span className="absolute right-1 top-1 flex gap-1">
                    {f.url && <button type="button" aria-label={`Mark up ${f.file.name}`} onClick={() => setMarking(f)} className="flex h-7 w-7 items-center justify-center rounded-md border-0 bg-white/95 text-ink shadow-btn"><PenLine aria-hidden size={14} /></button>}
                    <button type="button" aria-label={`Remove ${f.file.name}`} onClick={() => setFiles((cur) => cur.filter((x) => x.key !== f.key))} className="flex h-7 w-7 items-center justify-center rounded-md border-0 bg-white/95 text-ink shadow-btn"><X aria-hidden size={14} /></button>
                  </span>
                </div>
                <figcaption className="truncate text-[11px] text-ink-2">{f.file.name}{f.annotated && <span className="font-semibold text-bad"> · marked up</span>}</figcaption>
              </figure>
            ))}
            <button type="button" onClick={() => picker.current?.click()} className="flex min-h-24 flex-1 basis-[140px] flex-col items-center justify-center gap-1 border-0 bg-transparent text-center text-xs text-ink-2">
              <ImagePlus aria-hidden size={22} className="text-brand" />
              <span>Paste, drop, or <span className="font-semibold text-brand underline">choose files</span></span>
            </button>
            <input ref={picker} type="file" multiple accept={FILE_ACCEPT} className="hidden" onChange={(e) => { add([...(e.target.files ?? [])]); e.target.value = ''; }} />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Select label="Area" value={area} onChange={(e) => setArea(e.target.value)}>
            <option value="">Not set</option>{AREAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
          {bug && (
            <Select label="Found in" value={foundIn} onChange={(e) => setFoundIn(e.target.value)}>
              <option value="production">Production</option><option value="test">Test</option><option value="local">Local</option>
            </Select>
          )}
          <Select label="Owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
            <option value="">Unassigned</option>{team.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          <fieldset className="m-0 border-0 p-0"><legend className="mb-2 p-0 text-[15px] font-semibold">Priority</legend>
            <div className="flex flex-wrap gap-2">{PRIORITIES.map((p) => <ChoiceChip key={p} pressed={priority === p} onClick={() => setPriority(p)}>{PRIORITY_LABEL[p]}</ChoiceChip>)}</div></fieldset>
          <fieldset className="m-0 border-0 p-0"><legend className="mb-2 p-0 text-[15px] font-semibold">Size <span className="font-normal text-muted">(optional)</span></legend>
            <div className="flex gap-2">{(['s', 'm', 'l'] as Size[]).map((s) => <ChoiceChip key={s} pressed={size === s} onClick={() => setSize(size === s ? null : s)}>{SIZE_LABEL[s]}</ChoiceChip>)}</div></fieldset>
        </div>
        {kind !== 'epic' && !subtask && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Select label="Epic" value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">No epic</option>{epics.map((e) => <option key={e.id} value={e.id}>{e.reference} · {e.title}</option>)}
            </Select>
            <Select label="Ships in" value={releaseId} onChange={(e) => setReleaseId(e.target.value)}>
              <option value="">Not scheduled: keep it in the backlog</option>
              {releases.map((r) => <option key={r.id} value={r.id}>{releaseOption(r)}</option>)}
            </Select>
          </div>
        )}
        {kind === 'epic' && <p className="m-0 text-[13px] text-muted">An epic groups features, bugs, improvements and tasks. It isn't scheduled itself: you put its items in releases.</p>}
        {subtask && <p className="m-0 text-[13px] text-muted">This is a sub-task. It ships with its parent.</p>}
        <div className="flex flex-col gap-1.5">
          <TextField label="Link firm feedback" optional hint="The firms who asked hear when it ships." placeholder="FB- number or words" value={q} onChange={(e) => setQ(e.target.value)} />
          {matches.length > 0 && (
            <ul className="m-0 list-none divide-y divide-line-soft rounded-[10px] border border-line p-0">
              {matches.filter((m) => !requests.some((r) => r.id === m.id)).map((m) => (
                <li key={m.id}><button type="button" onClick={() => { setRequests((cur) => [...cur, { id: m.id, reference: m.reference, title: m.title, firm: firmLabel(m), status: m.status, estimate: m.estimated_completion }]); setQ(''); }}
                  className="w-full border-0 bg-transparent px-3 py-2 text-left text-[13px] hover:bg-row-hi"><span className="font-bold text-brand">{m.reference}</span> {m.title} · {firmLabel(m)}</button></li>
              ))}
            </ul>
          )}
          {requests.length > 0 && <div className="flex flex-wrap gap-1.5">{requests.map((r) => <TokenChip key={r.id} onRemove={() => setRequests((cur) => cur.filter((x) => x.id !== r.id))} removeLabel={`Unlink ${r.reference}`}>{r.reference} · {r.firm.split(' · ')[0]}</TokenChip>)}</div>}
          {requests.length > 0 && (told.length > 0
            ? <NotifyNote to={[`the people on ${told.map((r) => r.reference).join(', ')}`]} what={`estimated completion ${fmtCalendarDate(dated!)}`} />
            : <NotifyNote to={null} what="They hear when it ships" />)}
        </div>
      </div>
      {marking?.url && (
        <div className="fixed inset-0 z-[70] flex flex-col gap-3 bg-[#11161D] p-5 text-white"
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setMarking(null); } }}>
          <div className="text-sm font-bold">Mark up {marking.file.name}</div>
          <MarkupEditor src={marking.url} onCancel={() => setMarking(null)} onSave={(png) => {
            const name = marking.file.name.replace(/\.[^.]+$/, '') + '-marked.png';
            const file = new File([png], name, { type: 'image/png' });
            setFiles((cur) => cur.map((x) => (x.key === marking.key ? { ...x, file, url: URL.createObjectURL(file), annotated: true } : x)));
            setMarking(null);
          }} />
        </div>
      )}
    </Dialog>
  );
}
